import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Check, ExternalLink, Loader2, Pencil, Trash2, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { StoreMediaAdmin } from "@/components/admin/store-media-admin";
import { adjustBalance, adminDelete, adminUpdate, logAdminAction } from "@/lib/admin";
import { azError } from "@/lib/az-errors";
import { supabase } from "@/integrations/supabase/client";
import { storePlanLabels, storeSlug, storeStatusLabels, type Store } from "@/lib/stores";

export const Route = createFileRoute("/_authenticated/admin/magaza-tesdiqleri")({
  head: () => ({
    meta: [
      { title: "Mağaza təsdiqləri — Albazar.az" },
      {
        name: "description",
        content: "Yeni mağaza müraciətlərini yoxlayın, təsdiqləyin, rədd edin və redaktə edin.",
      },
      { property: "og:title", content: "Mağaza təsdiqləri — Albazar.az" },
      { property: "og:description", content: "Mağaza müraciətlərinin idarə edilməsi." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: StoreApprovalsPage,
});

async function fetchAllStores(): Promise<Store[]> {
  const { data, error } = await supabase
    .from("stores")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as Store[];
}

type EditState = {
  id: string;
  name: string;
  city: string;
  category: string;
  phone: string;
  whatsapp: string;
  description: string;
};

function StoreApprovalsPage() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<EditState | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({ queryKey: ["admin", "stores", "all"], queryFn: fetchAllStores });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "stores", "all"] });
    void qc.invalidateQueries({ queryKey: ["stores", "active"] });
  };

  const setStatus = useMutation({
    mutationFn: async ({ store, status }: { store: Store; status: string }) => {
      await adminUpdate("stores", store.id, {
        status,
        ...(status === "active" ? { verified: true } : {}),
      });
      await logAdminAction("store_status", "stores", store.id, { status });
    },
    onSuccess: (_d, v) => {
      toast.success(v.status === "active" ? "Mağaza təsdiqləndi" : "Mağaza rədd edildi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const remove = useMutation({
    mutationFn: async (store: Store) => {
      await adminDelete("stores", store.id);
      await logAdminAction("store_delete", "stores", store.id, { name: store.name });
    },
    onSuccess: () => {
      toast.success("Mağaza silindi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const pay = useMutation({
    mutationFn: async ({ store, amount, kind }: { store: Store; amount: number; kind: "topup" | "order" }) => {
      if (!store.owner_id) throw new Error("Mağazanın sahibi tapılmadı");
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("Düzgün məbləğ daxil edin");
      await adjustBalance(
        store.owner_id,
        amount,
        kind === "order"
          ? `Sifariş ödənişi — ${store.name}`
          : `Admin tərəfindən balans yükləmə — ${store.name}`,
        kind === "order" ? "order" : "topup",
      );
    },
    onSuccess: (_d, v) => {
      toast.success(
        v.kind === "order"
          ? "Sifariş ödənişi mağaza sahibinin balansına köçürüldü"
          : "Balans mağaza sahibinə yükləndi",
      );
      setAmounts((p) => ({ ...p, [v.store.id]: "" }));
      void qc.invalidateQueries({ queryKey: ["my-transactions"] });
      void qc.invalidateQueries({ queryKey: ["balance"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const save = useMutation({
    mutationFn: async (v: EditState) => {
      await adminUpdate("stores", v.id, {
        name: v.name.trim(),
        city: v.city.trim() || null,
        category: v.category.trim() || null,
        phone: v.phone.trim() || null,
        whatsapp: v.whatsapp.replace(/\D/g, "") || null,
        description: v.description.trim() || null,
      });
      await logAdminAction("store_edit", "stores", v.id, {});
    },
    onSuccess: () => {
      toast.success("Mağaza yeniləndi");
      setEdit(null);
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });


  if (isLoading) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  const rows = data ?? [];
  const pending = rows.filter((s) => s.status === "pending");
  const others = rows.filter((s) => s.status !== "pending");

  const Card = ({ store }: { store: Store }) => (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-start gap-3">
        <div className="size-12 shrink-0 overflow-hidden rounded-xl border border-border bg-muted">
          {store.logo_url ? (
            <img src={store.logo_url} alt={`${store.name} loqosu`} loading="lazy" className="size-full object-cover" />
          ) : (
            <span className="grid size-full place-items-center text-lg font-black text-primary">
              {store.name.slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="truncate text-sm font-bold">{store.name}</h3>
            {store.verified && <BadgeCheck className="size-4 text-info" />}
            <Badge
              className="rounded-full"
              variant={
                store.status === "active" ? "secondary" : store.status === "blocked" ? "destructive" : "outline"
              }
            >
              {storeStatusLabels[store.status] ?? store.status}
            </Badge>
            <Badge variant="outline" className="rounded-full">
              {storePlanLabels[store.plan] ?? store.plan}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {store.city ?? "Şəhər göstərilməyib"} · {store.category ?? "Kateqoriya yoxdur"} ·{" "}
            {store.phone ?? "Telefon yoxdur"}
          </p>
          {store.description && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{store.description}</p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button asChild size="sm" variant="outline" className="h-9 rounded-full text-xs">
          <Link to="/magaza/$slug" params={{ slug: storeSlug(store) }} target="_blank">
            <ExternalLink className="size-3.5" /> Mağazaya bax
          </Link>
        </Button>
        {store.status !== "active" && (
          <Button
            size="sm"
            className="h-9 rounded-full text-xs"
            disabled={setStatus.isPending}
            onClick={() => setStatus.mutate({ store, status: "active" })}
          >
            <Check className="size-3.5" /> Təsdiq et
          </Button>
        )}
        {store.status !== "blocked" && (
          <Button
            size="sm"
            variant="destructive"
            className="h-9 rounded-full text-xs"
            disabled={setStatus.isPending}
            onClick={() => setStatus.mutate({ store, status: "blocked" })}
          >
            <X className="size-3.5" /> Rədd et
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          className="h-9 rounded-full text-xs"
          onClick={() =>
            setEdit({
              id: store.id,
              name: store.name,
              city: store.city ?? "",
              category: store.category ?? "",
              phone: store.phone ?? "",
              whatsapp: store.whatsapp ?? "",
              description: store.description ?? "",
            })
          }
        >
          <Pencil className="size-3.5" /> Redaktə et
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 rounded-full border-destructive/50 text-xs text-destructive hover:bg-destructive/10"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(`"${store.name}" mağazası birdəfəlik silinsin?`)) remove.mutate(store);
          }}
        >
          <Trash2 className="size-3.5" /> Sil
        </Button>
      </div>

      {store.owner_id && (
        <div className="mt-3 rounded-2xl border border-border bg-muted/40 p-3">
          <p className="flex items-center gap-1.5 text-xs font-bold">
            <Wallet className="size-3.5 text-info" /> Mağaza sahibinin balansı
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Sifariş və elan təqdimatı üzrə pul alımını burada təsdiqləyin — məbləğ dərhal sahibin
            «Balans» səhifəsində görünür.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Input
              inputMode="decimal"
              value={amounts[store.id] ?? ""}
              onChange={(e) => setAmounts((p) => ({ ...p, [store.id]: e.target.value }))}
              placeholder="Məbləğ (₼)"
              className="h-9 w-32 rounded-full text-xs"
            />
            <Button
              size="sm"
              className="h-9 rounded-full text-xs"
              disabled={pay.isPending}
              onClick={() =>
                pay.mutate({ store, amount: Number(amounts[store.id] ?? 0), kind: "order" })
              }
            >
              Sifariş ödənişi
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-9 rounded-full text-xs"
              disabled={pay.isPending}
              onClick={() =>
                pay.mutate({ store, amount: Number(amounts[store.id] ?? 0), kind: "topup" })
              }
            >
              Balans yüklə
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <AdminPageHeader
        title="Mağaza təsdiqləri"
        description="Yeni mağaza müraciətlərini yoxlayın, təsdiqləyin və ya rədd edin."
      />

      <h2 className="mb-3 text-sm font-bold">Gözləyən müraciətlər ({pending.length})</h2>
      {pending.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Gözləyən mağaza müraciəti yoxdur.
        </div>
      ) : (
        <div className="grid gap-3">
          {pending.map((s) => (
            <Card key={s.id} store={s} />
          ))}
        </div>
      )}

      <StoreMediaAdmin />

      <h2 className="mb-3 mt-8 text-sm font-bold">Bütün mağazalar ({others.length})</h2>
      <div className="grid gap-3">
        {others.map((s) => (
          <Card key={s.id} store={s} />
        ))}
      </div>

      <Dialog open={Boolean(edit)} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mağazanı redaktə et</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="st-name">Mağaza adı</Label>
                <Input id="st-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="st-city">Şəhər</Label>
                  <Input id="st-city" value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="st-cat">Kateqoriya</Label>
                  <Input
                    id="st-cat"
                    value={edit.category}
                    onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="st-phone">Telefon</Label>
                  <Input
                    id="st-phone"
                    value={edit.phone}
                    onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="st-wa">WhatsApp</Label>
                  <Input id="st-wa" value={edit.whatsapp} onChange={(e) => setEdit({ ...edit, whatsapp: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="st-desc">Təsvir</Label>
                <Textarea
                  id="st-desc"
                  rows={4}
                  value={edit.description}
                  onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEdit(null)}>
              Ləğv et
            </Button>
            <Button
              className="rounded-full"
              disabled={save.isPending}
              onClick={() => edit && save.mutate(edit)}
            >
              {save.isPending && <Loader2 className="size-4 animate-spin" />} Yadda saxla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
